import { Children, ReactNode } from "react";

import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

/**
 * Stacks alternatives — "do this, *or* this" — with a labelled rule between
 * each one, centred in whatever it is given.
 *
 * Each child is one choice, and can be anything: a button, a line of guidance,
 * a dropzone. The separator is only ever drawn *between* choices, so a single
 * child renders alone and a conditional child that resolves to nothing does not
 * leave a stray rule behind.
 *
 * ```tsx
 * <OrChoices>
 *   <FileDropzone onDrop={onDrop} />
 *   <Button>Browse files</Button>
 * </OrChoices>
 * ```
 */
export function OrChoices({
  children,
  label = "or",
  className,
}: {
  children: ReactNode;
  /** Word set into the rule. Defaults to "or". */
  label?: string;
  className?: string;
}) {
  const choices = Children.toArray(children).filter(Boolean);

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2 w-full",
        className,
      )}
    >
      {choices.map((choice, index) => (
        <div key={index} className="contents">
          {index > 0 && (
            <div
              // Rules to either side of the label, never behind it. Short
              // enough to read as a separator between two choices rather than
              // a section break across the whole panel.
              className="flex items-center gap-2 w-full max-w-40 mt-1 mb-1"
            >
              <div className="flex-1 h-px bg-border" />
              <Typography level="body-sm" className="text-muted-foreground/80">
                {label}
              </Typography>
              <div className="flex-1 h-px bg-border" />
            </div>
          )}
          {choice}
        </div>
      ))}
    </div>
  );
}
